import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Shield, Award, Users, Globe, CheckCircle, Target } from 'lucide-react';
import { useSEO } from '../../utils/seo';

export default function About() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useSEO({
    title: 'Hakkımızda - IQCERT Belgelendirme ve Denetim | 15+ Yıllık Deneyim',
    description: 'IQCERT olarak 15 yılı aşkın deneyimimizle belgelendirme ve denetim alanında Türkiye\'nin önde gelen kuruluşlarından biriyiz. Kalite, güvenilirlik ve mükemmellik ilkelerimizle hizmet veriyoruz.',
    keywords: 'IQCERT hakkında, belgelendirme şirketi, ISO denetim, kurumsal değerler, akreditasyon',
    canonical: `${siteUrl}/about`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      '@id': `${siteUrl}/about#aboutpage`,
      url: `${siteUrl}/about`,
      name: 'Hakkımızda - IQCERT',
      description: 'IQCERT\'in misyonu, vizyonu, değerleri ve yönetim ekibi hakkında bilgiler',
      mainEntity: {
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
        name: 'IQCERT',
        foundingDate: '2009',
        numberOfEmployees: {
          '@type': 'QuantitativeValue',
          value: 50
        },
        slogan: 'Kalite, Güvenilirlik ve Mükemmellik',
        knowsAbout: [
          'ISO 9001 Kalite Yönetim Sistemi',
          'ISO 14001 Çevre Yönetim Sistemi',
          'ISO 45001 İş Sağlığı ve Güvenliği',
          'Belgelendirme Hizmetleri',
          'Denetim ve Gözetim'
        ]
      }
    }
  });

  const stats = [
    { number: '15+', label: 'Yıllık Deneyim' },
    { number: '5000+', label: 'Tamamlanan Proje' },
    { number: '500+', label: 'Mutlu Müşteri' },
    { number: '50+', label: 'Uzman Personel' }
  ];

  const values = [
    {
      icon: Shield,
      title: 'Güvenilirlik',
      description: 'Müşterilerimize en yüksek kalitede hizmet sunarak güvenlerini kazanıyoruz.'
    },
    {
      icon: Award,
      title: 'Mükemmellik',
      description: 'Her projede mükemmelliği hedefleyerek sektörde öncü olmaya devam ediyoruz.'
    },
    {
      icon: Users,
      title: 'Müşteri Odaklılık',
      description: 'Müşteri memnuniyetini her şeyin üstünde tutarak hizmet veriyoruz.'
    },
    {
      icon: Globe,
      title: 'Küresel Standartlar',
      description: 'Uluslararası standartlara uygun belgelendirme hizmetleri sunuyoruz.'
    }
  ];

  const team = [
    {
      name: 'Dr. Mehmet Yılmaz',
      position: 'Genel Müdür',
      image: 'https://public.readdy.ai/ai/img_res/35d4a2657a42d2da83121443926795b6.jpg',
      experience: '20 yıl deneyim'
    },
    {
      name: 'Ayşe Demir',
      position: 'Kalite Müdürü',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Turkish%20female%20quality%20manager%2C%20business%20attire%2C%20confident%20pose%2C%20modern%20office%20setting%2C%20corporate%20photography&width=300&height=400&seq=team2&orientation=portrait',
      experience: '15 yıl deneyim'
    },
    {
      name: 'İng. Ahmet Kaya',
      position: 'Teknik Müdür',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Turkish%20engineer%2C%20technical%20manager%2C%20business%20suit%2C%20engineering%20background%2C%20professional%20headshot%20photography&width=300&height=400&seq=team3&orientation=portrait',
      experience: '18 yıl deneyim'
    },
    {
      name: 'Fatma Özkan',
      position: 'İş Geliştirme Müdürü',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Turkish%20business%20development%20manager%2C%20female%20executive%2C%20modern%20business%20attire%2C%20office%20environment%2C%20corporate%20portrait&width=300&height=400&seq=team4&orientation=portrait',
      experience: '12 yıl deneyim'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-red-600 to-red-800 text-white py-20">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl font-bold mb-6">Hakkımızda</h1>
              <p className="text-xl leading-relaxed">
                IQCERT olarak, 15 yılı aşkın deneyimimizle belgelendirme ve denetim alanında 
                Türkiye'nin önde gelen kuruluşlarından biriyiz. Kalite, güvenilirlik ve 
                mükemmellik ilkelerimizle hizmet veriyoruz.
              </p>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl font-bold text-red-600 mb-2">{stat.number}</div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12">
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="flex items-center mb-6">
                  <Target className="w-8 h-8 text-red-600 mr-3" />
                  <h2 className="text-3xl font-bold text-gray-800">Misyonumuz</h2>
                </div>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Müşterilerimize en yüksek kalitede belgelendirme, denetim ve danışmanlık 
                  hizmetleri sunarak, onların rekabet gücünü artırmak ve sürdürülebilir 
                  büyümelerine katkıda bulunmak. Uluslararası standartlara uygun, güvenilir 
                  ve etkin çözümler üreterek sektörde öncülük etmek.
                </p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="flex items-center mb-6">
                  <CheckCircle className="w-8 h-8 text-red-600 mr-3" />
                  <h2 className="text-3xl font-bold text-gray-800">Vizyonumuz</h2>
                </div>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Belgelendirme ve denetim alanında Türkiye'nin en güvenilir ve tercih edilen 
                  kuruluşu olmak. Teknolojik gelişmeleri takip ederek, yenilikçi yaklaşımlarla 
                  müşteri memnuniyetini en üst düzeyde tutmak ve küresel pazarda tanınan bir 
                  marka haline gelmek.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Değerlerimiz</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                IQCERT olarak, tüm faaliyetlerimizde rehber aldığımız temel değerlerimiz
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-lg text-center hover:shadow-xl transition-shadow">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Yönetim Ekibimiz</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Alanında uzman, deneyimli ve yetkin kadromuzla hizmet veriyoruz
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {team.map((member, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-64 object-cover object-top"
                  />
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{member.name}</h3>
                    <p className="text-red-600 font-medium mb-2">{member.position}</p>
                    <p className="text-gray-600 text-sm">{member.experience}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Certifications */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Akreditasyonlarımız</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Uluslararası standartlarda hizmet verebilmek için sahip olduğumuz akreditasyonlar
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <img 
                  src="https://static.readdy.ai/image/aaba9897d2facfbc7f1204630a4017ac/a61e7e849e28851168395ea04f6e5205.jpeg"
                  alt="ULAK Akreditasyonu - Uluslararası Laboratuvar Akreditasyon Kurumu"
                  title="ULAK Akreditasyon Belgesi"
                  className="w-32 h-24 object-contain mx-auto mb-4"
                />
                <h3 className="text-xl font-bold text-gray-800 mb-2">ULAK Akreditasyonu</h3>
                <p className="text-gray-600">Uluslararası Laboratuvar Akreditasyon Kurumu onaylı belgelendirme</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=ISO%20certification%20logo%2C%20international%20standards%20organization%20badge%2C%20quality%20management%20system%20symbol&width=200&height=150&seq=cert2&orientation=landscape"
                  alt="ISO Sertifikası"
                  className="w-32 h-24 object-contain mx-auto mb-4"
                />
                <h3 className="text-xl font-bold text-gray-800 mb-2">ISO Sertifikaları</h3>
                <p className="text-gray-600">Uluslararası standartlara uygunluk sertifikaları</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=European%20accreditation%20logo%2C%20EA%20certification%20badge%2C%20international%20quality%20standards%20symbol&width=200&height=150&seq=cert3&orientation=landscape"
                  alt="Avrupa Akreditasyonu"
                  className="w-32 h-24 object-contain mx-auto mb-4"
                />
                <h3 className="text-xl font-bold text-gray-800 mb-2">EA Üyeliği</h3>
                <p className="text-gray-600">Avrupa Akreditasyon Birliği üyeliği</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
